import { css } from '@emotion/react';
import { Entry } from '@weave/schema-gen-ts/dist/schemas/schedule/quickfill/v2/quickfill.pb';
import { ScheduleQueries } from '@frontend/api-schedule';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useContactPanelShallowStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Avatar, Button, Chip, formatDate, SpinningLoader, Text, useAlert } from '@frontend/design-system';
import { TruncatedText } from '../../../../components/TruncatedText';
import { getFullName } from '../../../../utils';
import { quickfillManageListTrackingIds } from '../../tracking-ids';
import { OptOutIcon } from '../components/opt-out-icon';

interface Props {
  recipient: Entry;
  locationId: string;
}

export const QuickFillListItem = ({ recipient, locationId }: Props) => {
  const { t } = useTranslation('schedule-quick-fill');
  const alert = useAlert();
  const { setPersonId } = useContactPanelShallowStore('setPersonId');

  const { note, person, personId, validTo } = recipient;
  const firstName = person?.firstName?.trim() || '';
  const lastName = person?.lastName?.trim() || '';
  const isOptOut = false; // @TODO: Update this to `person?.receiveSms === false` once opt-out response is fixed by BE

  const { mutateAsync: deleteAsync, isLoading: isDeleting } =
    ScheduleQueries.useMutateDeleteQuickFillRecipient(locationId);

  const handleRemove = () => {
    if (!personId) return;

    deleteAsync(personId)
      .then(() => alert.success(t('Quick Fill Recipient Deleted')))
      .catch(() => alert.error(t('Error Deleting Quick Fill Recipient')));
  };

  return (
    <div css={styles.root(isDeleting)}>
      <div css={[styles.summary]}>
        <Avatar
          size='large'
          firstName={firstName}
          lastName={lastName}
          onClick={() => setPersonId(recipient?.personId || '')}
        />
        <div>
          <div css={styles.details}>
            <TruncatedText tooltipLabel={getFullName(person)} hideTooltip>
              <strong>{firstName}</strong> {lastName}
            </TruncatedText>
            {isOptOut && <OptOutIcon />}
          </div>
          {note && (
            <Text size='medium' color='subdued'>
              {note}
            </Text>
          )}
        </div>
      </div>
      <div css={styles.flexAlignCenter}>
        {validTo && (
          <Chip tooltipLabel={t(`Appointment on {{date}}`, { date: formatDate(validTo, 'MM/DD/YY') })}>
            <Icon name='calendar-small' color='primary' />
          </Chip>
        )}
        {isDeleting ? (
          <SpinningLoader size={'small'} />
        ) : (
          <Button
            variant='secondary'
            iconName='x-small'
            label={t('Remove Quick Fill Recipient')}
            trackingId={quickfillManageListTrackingIds.removeQuickfillRecipientButton}
            onClick={handleRemove}
          />
        )}
      </div>
    </div>
  );
};

const styles = {
  root: (isDeleting: boolean) => css`
    padding: ${theme.spacing(1, 2)};
    border-top: 1px solid ${theme.colors.neutral20};
    display: flex;
    align-items: center;
    justify-content: space-between;
    opacity: ${isDeleting ? 0.5 : 1};
  `,
  summary: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    max-width: calc(100% - 82px); // Total width - width of the right side icons 32px + 34px and margin + 16px
  `,
  details: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    max-width: 264px;
  `,
  flexAlignCenter: css`
    display: flex;
    align-items: center;
  `,
};
